<template>
  <div class="full-width">
    <div
      ref="dropdownRef"
      class="p-multi-select p-multi-select--medium"
      :status="status"
      :focused="isOpen"
      @click="toggleMenu"
    >
      <div class="p-multi-select__cover">
        <div class="p-multi-select__inner">
          <p-text-input
            :value="displayValues"
            :name="id"
            :placeholder="label"
            :error="props.error"
            readonly
            @click="toggleMenu"
          >
            <template #append>
              <p-icon
                icon="chevron-down"
                size="sm"
                color="fg-secondary"
                :class="['p-select__icon mr-2', { 'p-select__icon--reverse': isOpen }]"
              />
            </template>
          </p-text-input>
        </div>
      </div>

      <!-- desktop dropdown -->
      <div v-if="isOpen && $device.isDesktop" class="p-multi-select__menu" @click.stop>
        <div v-if="props.searchable" class="p-12 p-multi-select__search-input">
          <p-search-input v-model="searchQuery" size="md" placeholder="" />
        </div>
        <div>
          <div
            v-for="(option, index) in filteredOptions"
            :key="index"
            class="p-12 p-multi-select__menu-content"
          >
            <p-check-box
              :key="option?.value"
              v-model="checkboxValues[option?.value]"
              :name="props?.name"
              @on-change="(val) => (checkboxValues[option?.value] = val)"
            >
              <template #append>
                <div class="place-items-start align-items-start gap-8" @click.stop>
                  <nuxt-img
                    v-if="option?.icon"
                    class="cursor-default"
                    width="20"
                    height="20"
                    alt="currency-icon"
                    :src="
                      getAsset({
                        type: option?.type === 'crypto' ? 'coin' : 'currency',
                        asset: option?.icon,
                      })
                    "
                  />
                  <div>
                    <p class="f-utility-14 t-fg-primary cursor-default" v-text="option?.title" />
                    <p
                      v-if="option?.symbol"
                      class="f-utility-12 t-fg-tertiary cursor-default"
                      v-text="option?.symbol?.toUpperCase()"
                    />
                  </div>
                </div>
              </template>
            </p-check-box>
          </div>
        </div>
        <div class="pb-8 px-12 p-multi-select__overflow-button">
          <p-button class="full-width" button-type="ghost" size="sm" @click="applyHandler">
            <p class="t-fg-accent" v-text="$t('general.apply')" />
          </p-button>
        </div>
      </div>
    </div>
    <!-- mobile bottom sheet -->
    <div v-if="$device.isMobile">
      <p-bottom-sheet
        v-if="isOpen"
        :title="`${label} filtrele`"
        show-close
        clear
        @on-clear="clearHandler"
        @on-close="isOpen = false"
      >
        <div @click.stop>
          <div v-if="props.searchable" class="p-multi-select__search-input p-8">
            <p-search-input v-model="searchQuery" size="md" placeholder="" />
          </div>
          <div>
            <div
              v-for="(option, index) in filteredOptions"
              :key="index"
              class="py-12 p-multi-select__menu-content"
            >
              <p-check-box
                :key="option?.value"
                v-model="checkboxValues[option?.value]"
                :name="props?.name"
              >
                <template #append>
                  <div class="place-items-start align-items-start gap-8" @click.stop>
                    <nuxt-img
                      v-if="option?.icon"
                      class="cursor-default"
                      width="20"
                      height="20"
                      alt="currency-icon"
                      :src="
                        getAsset({
                          type: option?.type === 'crypto' ? 'coin' : 'currency',
                          asset: option?.icon,
                        })
                      "
                    />
                    <div>
                      <p class="f-utility-14 t-fg-primary cursor-default" v-text="option?.title" />
                      <p
                        v-if="option?.symbol"
                        class="f-utility-12 t-fg-tertiary cursor-default"
                        v-text="option?.symbol?.toUpperCase()"
                      />
                    </div>
                  </div>
                </template>
              </p-check-box>
            </div>
          </div>
          <div class="py-8 p-multi-select__overflow-button">
            <p-button class="full-width" size="lg" @click="applyHandler">
              <p v-text="$t('general.apply')" />
            </p-button>
          </div>
        </div>
      </p-bottom-sheet>
    </div>
  </div>
</template>

<script setup lang="ts">
interface ModelValue {
  value: string
  desc?: string
  title?: string
  symbol?: string
  icon?: string
}

const props = defineProps({
  id: {
    type: String,
    default: 'Provide an ID',
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  options: {
    type: Array as () => ModelValue[],
    required: true,
  },
  status: {
    type: String,
    default: 'selectable',
    validator: (val: string) => ['disabled', 'readonly', 'selectable'].includes(val),
  },
  clear: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['on-change'])

const dropdownRef = ref<HTMLElement | null>(null)
const status = toRef(props, 'status')

const checkboxValues = ref({})
const checkboxAppliedValues = ref({})
const displayValues = ref('')
const numberOfSelectedOptions = ref(0)
const searchQuery = ref('')
const filteredOptions = ref()
const selectedValues = ref([])

const isOpen = ref<boolean>(false)

const initialCheckboxValues = computed(() => {
  return props.options.reduce((acc: Record<string | number, boolean>, option) => {
    acc[option.value] = false
    return acc
  }, {})
})

const clearHandler = () => {
  isOpen.value = false
  checkboxValues.value = { ...initialCheckboxValues.value }
  checkboxAppliedValues.value = { ...initialCheckboxValues.value }
  searchQuery.value = ''
  displayValues.value = ''
  numberOfSelectedOptions.value = 0
  selectedValues.value = []
  emit('on-change', [])
}

const applyHandler = () => {
  isOpen.value = false
  searchQuery.value = ''
  checkboxAppliedValues.value = { ...checkboxValues.value }

  const displayValuesArray = []
  for (const [key, value] of Object.entries({ ...checkboxValues.value })) {
    if (value) {
      const option = props.options.find((option) => option.value === key)
      displayValuesArray.push(option?.title)
      selectedValues.value.push(option?.value)
    }
  }

  if (displayValuesArray.length === 0) {
    displayValues.value = ''
    numberOfSelectedOptions.value = 0
  } else {
    displayValues.value = displayValuesArray?.join(', ')
    numberOfSelectedOptions.value = displayValuesArray.length
  }

  emit('on-change', selectedValues.value)
}

const toggleMenu = () => {
  props.status === 'selectable' && (isOpen.value = !isOpen.value)
}

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
    checkboxValues.value = { ...checkboxAppliedValues.value }
    searchQuery.value = ''
  }
}

const debounceSearch = $_.debounce(function () {
  if (searchQuery.value) {
    filteredOptions.value = props?.options?.filter((option) => {
      return (
        option?.title?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        option?.symbol?.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    })
  } else {
    filteredOptions.value = props.options
  }
}, 500)

watch(
  () => searchQuery.value,
  () => debounceSearch(),
)

watch(
  () => props.clear,
  () => clearHandler(),
  { immediate: true },
)

onMounted(() => {
  filteredOptions.value = props.options

  checkboxValues.value = { ...initialCheckboxValues.value }

  checkboxAppliedValues.value = { ...initialCheckboxValues.value }

  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
